import React, { useMemo, useState } from "react";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { toast } from "react-toastify";
import apiClient from "../api/apiClient";
import InfiniteScroll from "../components/InfiniteScroll";
import LoadingSpinner from "../components/LoadingSpinner";
import NotFound from "../components/NotFound";
import WarningModal from "../modals/WarningModal";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { handleErrors } from "../modules/handleErrors";

const PAGE_SIZE = 12;

const BRAND = {
  primary: "#C93312",
  accent: "#FF3E1F",
  peach: "#FAE1CE",
  warm: "#F8D4B7",
};

const formatDownloadDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function DownloadCard({ item, onDownload, onRemove }) {
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const formattedDate = formatDownloadDate(item?.created_at);

  return (
    <div className="col-lg-3 col-md-3 col-sm-4 col-6 mb-4">
      <div
        className="position-relative overflow-hidden"
        style={{
          borderRadius: "20px",
          aspectRatio: "1 / 1",
          backgroundColor: "#F1F3F5",
          boxShadow: isHovered
            ? "0 12px 28px rgba(201, 51, 18, 0.18)"
            : "0 4px 14px rgba(0, 0, 0, 0.08)",
          transform: isHovered ? "translateY(-4px)" : "translateY(0)",
          transition: "box-shadow 0.25s ease, transform 0.25s ease",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {item?.image && !imageError ? (
          <img
            src={item.image}
            alt="Downloaded mockup"
            className="w-100 h-100"
            style={{ objectFit: "cover", display: "block" }}
            onError={() => setImageError(true)}
          />
        ) : (
          <div
            className="w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2"
            style={{ color: "#ADB5BD" }}
          >
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 36 }} />
            <span style={{ fontSize: "12px" }}>Preview unavailable</span>
          </div>
        )}

        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-between p-2"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.55) 100%)",
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.25s ease",
            pointerEvents: isHovered ? "auto" : "none",
          }}
        >
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-sm border-0 d-flex align-items-center justify-content-center"
              aria-label="Remove download"
              onClick={() => onRemove(item?.id)}
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.92)",
                color: BRAND.primary,
              }}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </button>
          </div>

          <div className="d-flex align-items-end justify-content-between gap-2">
            {formattedDate ? (
              <span
                className="text-white px-2 py-1 rounded-pill"
                style={{
                  fontSize: "11px",
                  backgroundColor: "rgba(0, 0, 0, 0.45)",
                  backdropFilter: "blur(4px)",
                }}
              >
                {formattedDate}
              </span>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="btn btn-sm border-0 d-flex align-items-center gap-1"
              onClick={() => onDownload(item?.image)}
              style={{
                borderRadius: "999px",
                backgroundColor: BRAND.primary,
                color: "#fff",
                fontSize: "12px",
                fontWeight: 600,
                padding: "6px 12px",
              }}
            >
              <CloudDownloadOutlinedIcon sx={{ fontSize: 16 }} />
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const MyDownloads = () => {
  const [showRemove, setShowRemove] = useState(false);
  const [downloadId, setDownloadId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const downloadImage = async (imageUrl) => {
    if (!imageUrl) {
      toast.error("Image not available for download");
      return;
    }

    const currentTime = new Date().toISOString().replace(/[:.]/g, "-");
    const fileName = `mockee-download-${currentTime}.jpg`;

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (error) {
      console.error("Error downloading the image:", error);
      toast.error("Failed to download image");
    }
  };

  const fetchDownloads = useMemo(
    () => async (page) =>
      apiClient.get(`/downloads/?page=${page}&page_size=${PAGE_SIZE}`),
    []
  );

  const {
    data: downloads,
    loading,
    hasMore,
    loadMore,
    refetch,
    totalCount,
  } = useInfiniteScroll(fetchDownloads, [], PAGE_SIZE);

  const openRemoveModal = (id) => {
    setDownloadId(id);
    setShowRemove(true);
  };

  const closeRemoveModal = () => {
    if (deleting) return;
    setShowRemove(false);
    setDownloadId(null);
  };

  const handleDelete = async () => {
    if (!downloadId) return;

    try {
      setDeleting(true);
      const response = await apiClient.delete(`/downloads/${downloadId}/`);
      setDeleting(false);

      if (!response.ok) return handleErrors(response.data);

      toast.success("Download removed");
      refetch();
      closeRemoveModal();
    } catch (error) {
      setDeleting(false);
      toast.error("Failed to remove download");
    }
  };

  const loadedCount = downloads?.length || 0;
  const showInitialLoader = loading && loadedCount === 0;

  return (
    <>
      <section className="mb-4">
        <div className="container">
          <div
            className="p-4 p-md-5"
            style={{
              background: `linear-gradient(135deg, ${BRAND.peach} 0%, ${BRAND.warm} 100%)`,
              borderTopRightRadius: "48px",
              borderBottomLeftRadius: "48px",
            }}
          >
            <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
              <div>
                <h2 className="mb-2 fw-bold" style={{ color: "#000000e6" }}>
                  My Downloads
                </h2>
                <p className="mb-0" style={{ color: "#4A4A4A", maxWidth: "520px" }}>
                  Your recently saved mockups are stored here for quick access.
                  We keep your last 500 downloads ready whenever you need them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-5 mb-4">
        <div className="container">
          {showInitialLoader ? (
            <div className="d-flex justify-content-center mt-5">
              <LoadingSpinner color={BRAND.accent} />
            </div>
          ) : loadedCount > 0 ? (
            <InfiniteScroll
              onLoadMore={loadMore}
              hasMore={hasMore}
              loading={loading}
              loader={<LoadingSpinner color={BRAND.accent} />}
            >
              <div className="row">
                {downloads.map((item) => (
                  <DownloadCard
                    key={item.id}
                    item={item}
                    onDownload={downloadImage}
                    onRemove={openRemoveModal}
                  />
                ))}
              </div>
            </InfiniteScroll>
          ) : (
            <NotFound
              emptyMessage="No downloads yet. Edit a mockup and save it to see it here."
            />
          )}
        </div>
      </section>

      <WarningModal
        showModal={showRemove}
        modalClose={closeRemoveModal}
        setConfirmClose={handleDelete}
        text="Are you sure you want to remove this download from your library?"
        confirmDisabled={deleting}
        confirmLabel={deleting ? "Removing..." : "Remove"}
      />
    </>
  );
};

export default MyDownloads;
